import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, ArrowLeft, MapPin } from "lucide-react";
import { format, parseISO, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, addMonths } from "date-fns";

export default function BookProvider() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [provider, setProvider] = useState(null);
  const [slots, setSlots] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/users/${id}`).then(r => setProvider(r.data)).catch(() => setProvider(null)),
      api.get(`/slots?provider_id=${id}&status=available`).then(r => setSlots(r.data)).catch(() => setSlots([])),
    ]);
  }, [id]);

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [month]);

  const slotsForDay = (day) => slots.filter(s => {
    try { return isSameDay(parseISO(s.start), day); } catch { return false; }
  });

  const request = async () => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (!pickedSlot) return;
    setBusy(true);
    try {
      await api.post("/bookings", { slot_id: pickedSlot.id, message: message || null });
      toast.success(t("booking_requested"));
      setPickedSlot(null);
      setMessage("");
      // refresh slots
      const { data } = await api.get(`/slots?provider_id=${id}&status=available`);
      setSlots(data);
      nav("/bookings/mine");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  if (!provider) return <div className="pt-32 text-center text-sm text-[#1A1F1C]/60">Loading...</div>;

  return (
    <div data-testid="book-provider-page" className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <Link to={`/users/${id}`} className="inline-flex items-center gap-2 text-sm text-[#143122] hover:underline mb-6">
          <ArrowLeft size={14} strokeWidth={1.5} /> {provider.name}
        </Link>

        <div className="overline text-[#143122] flex items-center gap-2">
          <CalendarIcon size={12} strokeWidth={1.5} /> {t("book_a_slot")}
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl text-[#143122]">
          {t("book_with")} {provider.name}
        </h1>
        {provider.location && (
          <div className="mt-2 flex items-center gap-1.5 text-sm text-[#1A1F1C]/70">
            <MapPin size={12} strokeWidth={1.5} /> {provider.location}
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-7 border border-[#E6E2D8] rounded-lg p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <button data-testid="prev-month" onClick={() => setMonth(addMonths(month, -1))} className="text-sm text-[#143122] hover:underline">← {t("prev")}</button>
              <div className="font-serif-display text-2xl text-[#143122]" data-testid="month-label">{format(month, "MMMM yyyy")}</div>
              <button data-testid="next-month" onClick={() => setMonth(addMonths(month, 1))} className="text-sm text-[#143122] hover:underline">{t("next")} →</button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-[#1A1F1C]/50 mb-2">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {monthDays.map(day => {
                const inMonth = day.getMonth() === month.getMonth();
                const daySlots = slotsForDay(day);
                const isSel = selectedDay && isSameDay(day, selectedDay);
                const hasSlots = daySlots.length > 0;
                return (
                  <button
                    key={day.toISOString()}
                    data-testid={`day-${format(day, "yyyy-MM-dd")}`}
                    onClick={() => setSelectedDay(day)}
                    disabled={!hasSlots}
                    className={`aspect-square rounded-md text-sm flex flex-col items-center justify-center transition-colors border ${
                      isSel ? "bg-[#143122] text-white border-[#143122]" :
                      hasSlots ? "bg-[#F4F1E9] border-[#E6E2D8] hover:border-[#143122] text-[#1A1F1C]" :
                      "border-transparent text-[#1A1F1C]/30 cursor-not-allowed"
                    } ${!inMonth ? "opacity-40" : ""}`}
                  >
                    <span>{format(day, "d")}</span>
                    {hasSlots && <span className={`mt-0.5 h-1 w-1 rounded-full ${isSel ? "bg-white" : "bg-[#143122]"}`} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Slots panel */}
          <div className="lg:col-span-5">
            {!selectedDay ? (
              <div className="border border-dashed border-[#E6E2D8] rounded-lg p-8 text-center text-sm text-[#1A1F1C]/60">
                {t("pick_a_day")}
              </div>
            ) : (
              <div className="border border-[#E6E2D8] rounded-lg p-6 bg-white">
                <div className="overline text-[#143122]">{format(selectedDay, "EEE d MMMM")}</div>
                <h3 className="mt-1 font-serif-display text-2xl text-[#143122]">{t("available_slots")}</h3>
                <div className="mt-4 space-y-2">
                  {slotsForDay(selectedDay).map(s => (
                    <button
                      key={s.id}
                      data-testid={`pick-slot-${s.id}`}
                      onClick={() => setPickedSlot(s)}
                      data-selected={pickedSlot?.id === s.id}
                      className={`w-full text-left border rounded-md p-3 transition-colors ${
                        pickedSlot?.id === s.id
                          ? "border-[#143122] bg-[#143122] text-white"
                          : "border-[#E6E2D8] hover:border-[#143122]"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <Clock size={14} strokeWidth={1.5} />
                        <span className="font-medium">{format(parseISO(s.start), "HH:mm")} – {format(parseISO(s.end), "HH:mm")}</span>
                      </div>
                      {s.title && <div className={`mt-1 font-serif-display text-base ${pickedSlot?.id === s.id ? "text-white" : "text-[#1A1F1C]"}`}>{s.title}</div>}
                      {s.notes && <div className={`text-xs ${pickedSlot?.id === s.id ? "text-white/80" : "text-[#1A1F1C]/60"}`}>{s.notes}</div>}
                    </button>
                  ))}
                </div>

                {pickedSlot && (
                  <div className="mt-5 border-t border-[#E6E2D8] pt-5">
                    <label className="block text-sm font-medium mb-2">{t("message_optional")}</label>
                    <textarea
                      data-testid="booking-message"
                      rows={3}
                      className="kav-input"
                      placeholder={t("write_message")}
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                    />
                    <button
                      data-testid="request-booking-btn"
                      disabled={busy}
                      onClick={request}
                      className="mt-3 kav-btn-primary w-full justify-center"
                    >
                      {busy ? "..." : t("request_booking")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
